import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
    Autocomplete,
    Box,
    Button,
    Grid,
    IconButton,
    Paper,
    TextField,
    Tooltip as MuiTooltip,
    Typography,
} from "@mui/material";
// import MUIDataTable from "mui-datatables"; // Debug table hidden
import { useEffect, useMemo, useRef, useState } from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    ReferenceLine,
    Legend,
} from "recharts";

import MonthRangePicker from "./components/Fields/MonthRangePicker";
import LoadingOverlay from "./components/LoadingOverlay";
import MultiSelectWithApply from "./components/MultiSelectWithApply";
import PipelineCharts from "./components/PipelineCharts/PipelineCharts";
import { getDefaultMonthRange } from "./utils/monthRange";

// Metrics that support product breakdown (extracted for CustomTooltip)
const breakdownMetrics = [
    "new_carr",
    "gross_new_carr",
    "total_carr",
    "total_churn",
    "total_churn_ctrl",
    "total_churn_unctrl",
    "new_customers",
    "total_customers",
    "yoy_carr_growth",
    "l3m_gross_churn",
    "grr",
    "ttm_grr",
    "escalated_to_dev",
    "not_escalated",
    "total_tickets",
    "close_rate",
    "close_rate_by_amount",
];

// Tooltip component for charts - extracted to avoid recreation on each render
const CustomTooltip = ({ active, payload, label, metricFormat, showBreakdown, metricKey }) => {
    if (active && payload && payload.length) {
        const dataPoint = payload[0].payload;
        const allBreakdowns = dataPoint?.product_breakdown;
        const breakdown = allBreakdowns?.[metricKey];
        const hasBreakdown =
            showBreakdown &&
            breakdownMetrics.includes(metricKey) &&
            breakdown &&
            breakdown.length > 0;

        return (
            <Paper sx={{ p: 1, bgcolor: "rgba(255,255,255,0.95)", maxWidth: 300 }}>
                <Typography variant="body2" fontWeight="bold">
                    {label}
                </Typography>
                <Typography variant="body2">{metricFormat(payload[0].value)}</Typography>
                {hasBreakdown && (
                    <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #eee" }}>
                        <Typography
                            variant="caption"
                            fontWeight="bold"
                            sx={{ display: "block", mb: 0.5 }}
                        >
                            Product Breakdown:
                        </Typography>
                        {breakdown.map((item, idx) => (
                            <Typography key={idx} variant="caption" sx={{ display: "block" }}>
                                {item.product_name || item.netsuite_product_name}:{" "}
                                {metricFormat(item.value)}
                            </Typography>
                        ))}
                    </Box>
                )}
            </Paper>
        );
    }
    return null;
};

// Color palette for multi-product lines
const PRODUCT_COLORS = [
    "#156082",
    "#e63946",
    "#2a9d8f",
    "#e9c46a",
    "#9b59b6",
    "#f4a261",
    "#1abc9c",
    "#e74c3c",
    "#3498db",
    "#95a5a6",
];

// True when a row's period (a month-end date string) falls within the
// applied month range (YYYY-MM strings), inclusive
const isPeriodInRange = (dateStr, { startMonth, endMonth }) => {
    const d = new Date(dateStr);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    return ym >= startMonth && ym <= endMonth;
};

const ProductHealthTab = ({ fetchData }) => {
    const [healthData, setHealthData] = useState([]);
    const [perProductData, setPerProductData] = useState([]);
    const [isMultiProduct, setIsMultiProduct] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [productOptions, setProductOptions] = useState([]);
    const [productFamilyOptions, setProductFamilyOptions] = useState([]);
    const [businessUnitOptions, setBusinessUnitOptions] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [selectedProductFamily, setSelectedProductFamily] = useState(null);
    const [selectedBusinessUnit, setSelectedBusinessUnit] = useState(null);
    const [ticketEscalationData, setTicketEscalationData] = useState([]);
    const [perProductTicketData, setPerProductTicketData] = useState([]);
    const [isMultiProductTicket, setIsMultiProductTicket] = useState(false);
    const [closeRateData, setCloseRateData] = useState([]);
    const [closeRateLoaded, setCloseRateLoaded] = useState(false);
    const [healthDataLoaded, setHealthDataLoaded] = useState(false);
    const [cohortData, setCohortData] = useState([]);
    const [cohortLoaded, setCohortLoaded] = useState(false);
    // Filter drafts: the selectors update these; the Apply button copies them
    // into the applied state (selectedProducts/selectedProductFamily/
    // selectedBusinessUnit/appliedMonthRange) that drives fetching and windowing.
    // Default month range covers the 24 complete months ending with last month.
    const [draftProducts, setDraftProducts] = useState([]);
    const [draftProductFamily, setDraftProductFamily] = useState(null);
    const [draftBusinessUnit, setDraftBusinessUnit] = useState(null);
    const [draftMonthRange, setDraftMonthRange] = useState(() => getDefaultMonthRange(24));
    const [appliedMonthRange, setAppliedMonthRange] = useState(() => getDefaultMonthRange(24));

    const lastFetchKey = useRef(null);
    const lastCohortFetchKey = useRef(null);
    const lastTicketFetchKey = useRef(null);
    const closeRateFetched = useRef(false);
    const productsFetched = useRef(false);
    const familiesFetched = useRef(false);
    const initialFetchDone = useRef(false);

    // Request sequence tracking to prevent race conditions
    const ticketRequestId = useRef(0);
    const cohortRequestId = useRef(0);
    const healthRequestId = useRef(0);

    const getMaxWidth = (options, minWidth = 150) => {
        if (!options || options.length === 0) return minWidth;
        const maxLength = Math.max(...options.map((o) => (o.name || "").length));
        return Math.max(minWidth, maxLength * 8 + 60); // ~8px per char + padding for dropdown arrow
    };

    const formatCurrencyFull = (value) => {
        if (value === null || value === undefined) return "-";
        return `$${Number(value).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
    };

    const formatPercent = (value) => {
        if (value === null || value === undefined) return "-";
        return `${(Number(value) * 100).toFixed(1)}%`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        const date = new Date(dateStr);
        return date.toLocaleDateString("en-US", { year: "2-digit", month: "short" });
    };

    const fillGaps = (data) => {
        if (!data || data.length < 2) return data;

        // Sort by period
        const sorted = [...data].sort((a, b) => new Date(a.period) - new Date(b.period));

        // Get first row for product info
        const { product_name, product_family_name } = sorted[0];

        // Generate all months between min and max
        const minDate = new Date(sorted[0].period);
        const maxDate = new Date(sorted[sorted.length - 1].period);

        const allMonths = [];
        const current = new Date(minDate.getFullYear(), minDate.getMonth(), 1);
        while (current <= maxDate) {
            // Get last day of month
            const lastDay = new Date(current.getFullYear(), current.getMonth() + 1, 0);
            allMonths.push(lastDay.toISOString().split("T")[0]);
            current.setMonth(current.getMonth() + 1);
        }

        // Create a map of existing data by period (normalized to YYYY-MM-DD)
        const dataMap = new Map();
        sorted.forEach((row) => {
            const d = new Date(row.period);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            dataMap.set(key, row);
        });

        // Fill gaps
        const filled = [];
        let prevRow = null;

        allMonths.forEach((monthStr) => {
            const d = new Date(monthStr);
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

            if (dataMap.has(key)) {
                const row = { ...dataMap.get(key), isGap: false };
                filled.push(row);
                prevRow = row;
            } else if (prevRow) {
                // Create gap row with carried-forward values
                filled.push({
                    product_name,
                    product_family_name,
                    period: monthStr,
                    new_carr: 0,
                    gross_new_carr: 0,
                    total_carr: prevRow.total_carr,
                    total_churn: 0,
                    total_churn_ctrl: 0,
                    total_churn_unctrl: 0,
                    new_customers: 0,
                    total_customers: prevRow.total_customers,
                    yoy_carr_growth: null,
                    l3m_gross_churn: null,
                    grr: prevRow.total_carr ? 1 : null,
                    ttm_grr: null,
                    isGap: true,
                });
                prevRow = filled[filled.length - 1];
            }
        });

        // Recalculate YoY growth for all rows
        filled.forEach((row, idx) => {
            const currentDate = new Date(row.period);
            const targetYear = currentDate.getFullYear() - 1;
            const targetMonth = currentDate.getMonth();

            // Find row from 12 months ago
            const priorRow = filled.find((r) => {
                const d = new Date(r.period);
                return d.getFullYear() === targetYear && d.getMonth() === targetMonth;
            });

            if (priorRow && priorRow.total_carr && priorRow.total_carr !== 0) {
                row.yoy_carr_growth = (row.total_carr - priorRow.total_carr) / priorRow.total_carr;
            }
        });

        // Recalculate L3M gross churn for all rows
        filled.forEach((row, idx) => {
            if (idx < 2) return; // Need at least 3 months

            const m0 = filled[idx];
            const m1 = filled[idx - 1];
            const m2 = filled[idx - 2];

            const totalChurn3m =
                (m0.total_churn || 0) + (m1.total_churn || 0) + (m2.total_churn || 0);
            const baseCarr = (m2.total_carr || 0) - (m2.new_carr || 0);

            if (baseCarr !== 0) {
                row.l3m_gross_churn = totalChurn3m / baseCarr;
            }
        });

        // Recalculate TTM GRR for all rows (backend value is null when the month
        // 12 back had no data row; the gap-filled series always has one)
        filled.forEach((row, idx) => {
            if (idx < 12) return; // Need 12 months of history

            const openingCarr = filled[idx - 12].total_carr || 0;
            let ttmChurn = 0;
            for (let i = idx - 11; i <= idx; i++) {
                ttmChurn += filled[i].total_churn || 0;
            }

            if (openingCarr !== 0) {
                row.ttm_grr = (openingCarr + ttmChurn) / openingCarr;
            }
        });

        return filled;
    };

    // Fetch products and business units on mount
    useEffect(() => {
        if (productsFetched.current) return;
        productsFetched.current = true;

        fetchData("getproducts")
            .then((data) => {
                const products = (data.products || [])
                    .filter((p) => p.custrecord_ng_prd_org_grouping) // Exclude products without a family
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                setProductOptions(products);

                const businessUnits = (data.businessUnits || []).sort((a, b) =>
                    (a.name || "").localeCompare(b.name || "")
                );
                setBusinessUnitOptions(businessUnits);
            })
            .catch((error) => {
                console.error("Error fetching products:", error);
                setProductOptions([]);
                setBusinessUnitOptions([]);
            });
    }, [fetchData]);

    // Fetch product families on mount
    useEffect(() => {
        if (familiesFetched.current) return;
        familiesFetched.current = true;

        fetchData("getproductfamilies")
            .then((data) => {
                const productFamilies = (data.productFamilies || [])
                    .filter((f) => f.name !== "In Development - Do Not Use")
                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
                setProductFamilyOptions(productFamilies);
            })
            .catch((error) => {
                console.error("Error fetching product families:", error);
                setProductFamilyOptions([]);
            });
    }, [fetchData]);

    // Fetch ticket escalation data when product filters change
    useEffect(() => {
        const fetchKey =
            selectedProducts.length > 0
                ? `products-${selectedProducts.map((p) => p.id).join(",")}`
                : selectedProductFamily
                  ? `family-${selectedProductFamily.id}`
                  : selectedBusinessUnit
                    ? `businessunit-${selectedBusinessUnit.id}`
                    : "all";

        if (lastTicketFetchKey.current === fetchKey) {
            return;
        }
        lastTicketFetchKey.current = fetchKey;

        const currentRequestId = ++ticketRequestId.current;

        const params = {};
        if (selectedProducts.length > 0) {
            params.productids = selectedProducts.map((p) => p.id).join(",");
        } else if (selectedProductFamily) {
            params.productfamilyid = selectedProductFamily.id;
        } else if (selectedBusinessUnit) {
            params.businessunitid = selectedBusinessUnit.id;
        }

        fetchData("getticketescalationreport", params)
            .then((data) => {
                // Ignore stale responses
                if (currentRequestId !== ticketRequestId.current) return;

                // Handle multi-product ticket data (when 2+ products selected)
                if (data.isMultiProduct && data.perProductData) {
                    const perProductFormatted = data.perProductData.map((row) => ({
                        ...row,
                        periodLabel: formatDate(row.period),
                    }));
                    setPerProductTicketData(perProductFormatted);
                    setIsMultiProductTicket(true);
                    setTicketEscalationData([]);
                } else {
                    const values = data.values || [];
                    const formatted = values.map((row) => ({
                        ...row,
                        periodLabel: formatDate(row.period),
                    }));
                    setTicketEscalationData(formatted);
                    setPerProductTicketData([]);
                    setIsMultiProductTicket(false);
                }
            })
            .catch((error) => {
                if (currentRequestId !== ticketRequestId.current) return;
                console.error("Error fetching ticket escalation data:", error);
                setTicketEscalationData([]);
                setPerProductTicketData([]);
                setIsMultiProductTicket(false);
            });
    }, [fetchData, selectedProducts, selectedProductFamily, selectedBusinessUnit]);

    // Fetch close rate data on mount (HubSpot data, not filtered by product)
    // Fetches year by year for past 5 years if cache miss, then caches merged data
    useEffect(() => {
        if (closeRateFetched.current) return;
        closeRateFetched.current = true;

        const fetchYearByYear = async () => {
            const currentYear = new Date().getFullYear();
            const yearsToFetch = [
                currentYear,
                currentYear - 1,
                currentYear - 2,
                currentYear - 3,
                currentYear - 4,
            ];
            const allValues = [];

            for (const year of yearsToFetch) {
                try {
                    console.log(`[CloseRate] Fetching year ${year}...`);
                    const data = await fetchData("getcloseratedata", { year });
                    if (data.values?.length > 0) {
                        allValues.push(...data.values);
                        console.log(`[CloseRate] Year ${year}: ${data.values.length} periods`);
                    }
                } catch (error) {
                    console.error(`[CloseRate] Error fetching year ${year}:`, error);
                }
            }

            return allValues;
        };

        const fetchCloseRateData = async () => {
            try {
                // Try cached data first
                const data = await fetchData("getcloseratedata");
                const values = data.values || [];

                if (values.length > 0) {
                    // Cache hit - use cached data
                    console.log(`[CloseRate] Cache hit: ${values.length} periods`);
                    return values;
                }

                if (data.cacheStatus === "miss") {
                    // Cache miss - fetch year by year
                    console.log("[CloseRate] Cache miss, fetching year by year...");
                    const yearByYearValues = await fetchYearByYear();

                    // Cache the merged data on server
                    if (yearByYearValues.length > 0) {
                        try {
                            await fetch("/api/ns/cache/closerate", {
                                method: "POST",
                                headers: { "Content-Type": "application/json" },
                                body: JSON.stringify({ values: yearByYearValues }),
                            });
                            console.log(
                                `[CloseRate] Cached ${yearByYearValues.length} periods on server`
                            );
                        } catch (cacheError) {
                            console.error("[CloseRate] Error caching data:", cacheError);
                        }
                    }

                    return yearByYearValues;
                }

                return values;
            } catch (error) {
                console.error("Error fetching close rate data:", error);
                return [];
            }
        };

        fetchCloseRateData()
            .then((values) => {
                // Sort by period (YYYY-MM format) ascending
                const sorted = [...values].sort((a, b) => a.period.localeCompare(b.period));
                const formatted = sorted.map((row) => ({
                    ...row,
                    periodLabel: formatDate(row.period),
                }));
                setCloseRateData(formatted);
            })
            .finally(() => {
                setCloseRateLoaded(true);
            });
    }, [fetchData]);

    // Transform close rate data with filtered/keyed product breakdown for tooltips
    const transformedCloseRateData = useMemo(() => {
        if (!closeRateData.length) return [];

        // Only transform when filtering by family or business unit (not individual products)
        if (selectedProducts.length > 0 || (!selectedProductFamily && !selectedBusinessUnit)) {
            return closeRateData;
        }

        return closeRateData.map((row) => {
            const originalBreakdown = row.product_breakdown || [];

            // Filter breakdown by family or BU
            const filtered = originalBreakdown.filter((p) => {
                if (selectedProductFamily) {
                    return String(p.product_family_id) === String(selectedProductFamily.id);
                }
                if (selectedBusinessUnit) {
                    return String(p.business_unit_id) === String(selectedBusinessUnit.id);
                }
                return true;
            });

            // Aggregate by product name (multiple HubSpot items may map to same NS product)
            const aggregatedByProduct = {};
            filtered.forEach((p) => {
                const name = p.netsuite_product_name || p.hubspot_product_name || "Unknown";
                if (!aggregatedByProduct[name]) {
                    aggregatedByProduct[name] = {
                        closed_won: 0,
                        closed_lost: 0,
                        amount_won: 0,
                        amount_lost: 0,
                    };
                }
                aggregatedByProduct[name].closed_won += p.closed_won || 0;
                aggregatedByProduct[name].closed_lost += p.closed_lost || 0;
                aggregatedByProduct[name].amount_won += p.amount_won || 0;
                aggregatedByProduct[name].amount_lost += p.amount_lost || 0;
            });

            // Calculate close rates for aggregated products
            const aggregatedBreakdown = Object.entries(aggregatedByProduct).map(([name, data]) => {
                const totalCount = data.closed_won + data.closed_lost;
                const totalAmount = data.amount_won + data.amount_lost;
                return {
                    product_name: name,
                    close_rate: totalCount > 0 ? data.closed_won / totalCount : null,
                    close_rate_by_amount: totalAmount > 0 ? data.amount_won / totalAmount : null,
                };
            });

            // Return with both original array (for aggregation) and keyed format (for tooltips)
            return {
                ...row,
                // Keep original array format for aggregateProductBreakdown
                product_breakdown: filtered,
                // Add keyed format for tooltip display (aggregated by product)
                product_breakdown_keyed: {
                    close_rate: aggregatedBreakdown.map((p) => ({
                        product_name: p.product_name,
                        value: p.close_rate,
                    })),
                    close_rate_by_amount: aggregatedBreakdown.map((p) => ({
                        product_name: p.product_name,
                        value: p.close_rate_by_amount,
                    })),
                },
            };
        });
    }, [closeRateData, selectedProducts, selectedProductFamily, selectedBusinessUnit]);

    // Close rate rows within the applied month range
    const windowedCloseRateData = useMemo(
        () =>
            transformedCloseRateData.filter((row) =>
                isPeriodInRange(row.period, appliedMonthRange)
            ),
        [transformedCloseRateData, appliedMonthRange]
    );

    // Cohort rows within the applied month range (cohort is already YYYY-MM)
    const windowedCohortData = useMemo(
        () =>
            cohortData.filter(
                (row) =>
                    row.cohort >= appliedMonthRange.startMonth &&
                    row.cohort <= appliedMonthRange.endMonth
            ),
        [cohortData, appliedMonthRange]
    );

    // Fetch cohort retention data on mount and when filters change
    // When no filters: uses cached merged data from server
    // When filters applied: fetches year by year and merges client-side
    useEffect(() => {
        const hasFilters =
            selectedProducts.length > 0 || selectedProductFamily || selectedBusinessUnit;
        const fetchKey = hasFilters
            ? selectedProducts.length > 0
                ? `products-${selectedProducts.map((p) => p.id).join(",")}`
                : selectedProductFamily
                  ? `family-${selectedProductFamily.id}`
                  : `businessunit-${selectedBusinessUnit.id}`
            : "all";

        if (lastCohortFetchKey.current === fetchKey) {
            return;
        }
        lastCohortFetchKey.current = fetchKey;

        const currentRequestId = ++cohortRequestId.current;

        const baseParams = {};
        if (selectedProducts.length > 0) {
            baseParams.products = JSON.stringify(selectedProducts.map((p) => p.id));
        } else if (selectedProductFamily) {
            baseParams.family = selectedProductFamily.id;
        } else if (selectedBusinessUnit) {
            baseParams.businessUnit = selectedBusinessUnit.id;
        }

        // Fetch year by year starting from current year, going backwards
        const fetchYearByYear = async () => {
            const allData = [];
            let year = new Date().getFullYear();
            const minYear = 2015; // Reasonable lower bound to prevent infinite loop

            while (year >= minYear) {
                if (currentRequestId !== cohortRequestId.current) return null;

                try {
                    const data = await fetchData("getcohortretention", {
                        ...baseParams,
                        year,
                    });
                    const values = data.values || [];

                    if (values.length === 0) {
                        // No more data, stop fetching
                        break;
                    }

                    allData.push(...values);
                    year--;
                } catch (error) {
                    console.error(`Error fetching cohort data for year ${year}:`, error);
                    break;
                }
            }

            return allData;
        };

        const fetchCohortData = async () => {
            if (!hasFilters) {
                // No filters - try cached merged data first
                try {
                    const data = await fetchData("getcohortretention", {});
                    const values = data.values || [];
                    if (values.length > 0) {
                        return { values, fromCache: true };
                    }
                } catch (error) {
                    console.error("Error fetching cached cohort data:", error);
                }
                // Cache miss or empty - fall through to year-by-year
            }
            // Filters applied or cache miss - fetch year by year
            const values = await fetchYearByYear();
            return { values, fromCache: false };
        };

        fetchCohortData()
            .then(async (result) => {
                if (currentRequestId !== cohortRequestId.current) return;
                if (result === null || result.values === null) return; // Request was cancelled

                const { values: allData, fromCache } = result;
                setCohortData(allData);

                // Cache the merged data on server (only if fetched year-by-year without filters)
                if (!fromCache && !hasFilters && allData.length > 0) {
                    try {
                        await fetch("/api/ns/cache/cohort", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ values: allData }),
                        });
                    } catch (error) {
                        console.error("Error caching cohort data:", error);
                    }
                }
            })
            .catch((error) => {
                if (currentRequestId !== cohortRequestId.current) return;
                console.error("Error fetching cohort data:", error);
                setCohortData([]);
            })
            .finally(() => {
                if (currentRequestId !== cohortRequestId.current) return;
                setCohortLoaded(true);
            });
    }, [fetchData, selectedProducts, selectedProductFamily, selectedBusinessUnit]);

    // Set loading to false only when health data, close rate, and cohort data are loaded
    useEffect(() => {
        if (healthDataLoaded && closeRateLoaded && cohortLoaded) {
            setIsLoading(false);
        }
    }, [healthDataLoaded, closeRateLoaded, cohortLoaded]);

    // Handle product selection - clears product family and business unit
    const handleProductChange = (newValue) => {
        setDraftProducts(newValue);
        setDraftProductFamily(null);
        setDraftBusinessUnit(null);
    };

    // Handle product family selection - clears product and business unit
    const handleProductFamilyChange = (event, newValue) => {
        setDraftProductFamily(newValue);
        setDraftProducts([]);
        setDraftBusinessUnit(null);
    };

    // Handle business unit selection - clears product and product family
    const handleBusinessUnitChange = (event, newValue) => {
        setDraftBusinessUnit(newValue);
        setDraftProducts([]);
        setDraftProductFamily(null);
    };

    // Commit all filter drafts; the applied values trigger refetch/re-window
    const handleApplyFilters = () => {
        setSelectedProducts(draftProducts);
        setSelectedProductFamily(draftProductFamily);
        setSelectedBusinessUnit(draftBusinessUnit);
        setAppliedMonthRange(draftMonthRange);
    };

    // Fetch health data when product, product family, or business unit changes (or on initial load with no selection)
    useEffect(() => {
        const fetchKey =
            selectedProducts.length > 0
                ? `products-${selectedProducts.map((p) => p.id).join(",")}`
                : selectedProductFamily
                  ? `family-${selectedProductFamily.id}`
                  : selectedBusinessUnit
                    ? `businessunit-${selectedBusinessUnit.id}`
                    : "all";

        if (lastFetchKey.current === fetchKey) {
            return;
        }
        lastFetchKey.current = fetchKey;

        setIsLoading(true);
        setHealthDataLoaded(false);
        const currentRequestId = ++healthRequestId.current;
        const params =
            selectedProducts.length > 0
                ? { productids: selectedProducts.map((p) => p.id).join(",") }
                : selectedProductFamily
                  ? { productfamilyid: selectedProductFamily.id }
                  : selectedBusinessUnit
                    ? { businessunitid: selectedBusinessUnit.id }
                    : {};

        fetchData("getproducthealth", params)
            .then((data) => {
                // Ignore stale responses
                if (currentRequestId !== healthRequestId.current) return;

                const values = data.values || [];
                const filled = fillGaps(values);
                const formatted = filled.map((row) => ({
                    ...row,
                    periodLabel: formatDate(row.period),
                }));
                setHealthData(formatted);

                // Handle multi-product data for multiple lines per chart
                if (data.isMultiProduct && data.perProductData) {
                    const perProductFormatted = data.perProductData.map((row) => ({
                        ...row,
                        periodLabel: formatDate(row.period),
                    }));
                    setPerProductData(perProductFormatted);
                    setIsMultiProduct(true);
                } else {
                    setPerProductData([]);
                    setIsMultiProduct(false);
                }

                // Validate breakdown totals when filtering by family/BU
                if (
                    selectedProducts.length === 0 &&
                    (selectedProductFamily || selectedBusinessUnit)
                ) {
                    console.log("[ProductHealth] Validating breakdown totals...");

                    // CARR metrics: breakdown should equal main
                    const carrMetrics = ["new_carr", "gross_new_carr", "total_carr", "total_churn"];
                    carrMetrics.forEach((metricKey) => {
                        formatted.forEach((row) => {
                            const mainValue = row[metricKey];
                            const breakdown = row.product_breakdown?.[metricKey];
                            if (mainValue != null && breakdown?.length > 0) {
                                const breakdownSum = breakdown.reduce(
                                    (sum, item) => sum + (item.value || 0),
                                    0
                                );
                                if (Math.abs(breakdownSum - mainValue) > 0.01) {
                                    console.error(
                                        `[ProductHealth] ${metricKey} breakdown mismatch for ${row.periodLabel}: ` +
                                            `main=${mainValue}, breakdown sum=${breakdownSum}, ` +
                                            `diff=${mainValue - breakdownSum}`
                                    );
                                }
                            }
                        });
                    });

                    // Customer metrics: breakdown should be >= main (multi-product customers)
                    const customerMetrics = ["new_customers", "total_customers"];
                    customerMetrics.forEach((metricKey) => {
                        formatted.forEach((row) => {
                            const mainValue = row[metricKey];
                            const breakdown = row.product_breakdown?.[metricKey];
                            if (mainValue != null && breakdown?.length > 0) {
                                const breakdownSum = breakdown.reduce(
                                    (sum, item) => sum + (item.value || 0),
                                    0
                                );
                                if (breakdownSum < mainValue) {
                                    console.error(
                                        `[ProductHealth] ${metricKey} breakdown mismatch for ${row.periodLabel}: ` +
                                            `main=${mainValue}, breakdown sum=${breakdownSum}, ` +
                                            `missing=${mainValue - breakdownSum}`
                                    );
                                }
                            }
                        });
                    });

                    console.log("[ProductHealth] Breakdown validation complete.");
                }
            })
            .catch((error) => {
                // Ignore stale responses
                if (currentRequestId !== healthRequestId.current) return;

                console.error("Error fetching product health:", error);
                setHealthData([]);
            })
            .finally(() => {
                // Ignore stale responses
                if (currentRequestId !== healthRequestId.current) return;

                setHealthDataLoaded(true);
                initialFetchDone.current = true;
            });
    }, [fetchData, selectedProducts, selectedProductFamily, selectedBusinessUnit]);

    const chartColor = "#156082";
    const metrics = [
        {
            key: "new_customers",
            label: "New Customers",
            color: chartColor,
            format: (v) => v?.toLocaleString() || "-",
            description:
                "Count of customers who have a positive CARR value for the first time. When filtered by product the customer is counted in the month when they first had recurring license fees for that product. When viewed cumulatively at a company level, they are counted only once in the first month when they had a positive CARR for any product at Netgain.",
        },
        {
            key: "total_customers",
            label: "Total Customers",
            color: chartColor,
            format: (v) => v?.toLocaleString() || "-",
            description:
                "Count of unique customers with positive cumulative CARR at month-end. Customers are not counted twice when viewed at a product group or company level.",
        },
        {
            key: "new_carr",
            label: "Net New CARR",
            color: chartColor,
            format: formatCurrencyFull,
            showZeroLine: true,
            description:
                "Sum of all CARR adjustments for the month (positive and negative). This is gross new CARR minus churn. Can be negative if churn exceeds new bookings.",
        },
        {
            key: "gross_new_carr",
            label: "Gross New CARR",
            color: chartColor,
            format: formatCurrencyFull,
            description:
                "Sum of only positive CARR adjustments for the month. Includes new deals, expansions, and upsells before accounting for any churn.",
        },
        {
            key: "total_carr",
            label: "Total CARR",
            color: chartColor,
            format: formatCurrencyFull,
            description:
                "Running cumulative sum of all CARR adjustments up through the end of the month. Represents the total contracted annual recurring revenue at month-end.",
        },
        {
            key: "total_churn",
            label: "Total Churn",
            color: chartColor,
            format: formatCurrencyFull,
            showZeroLine: true,
            description:
                "Sum of all negative CARR adjustments for the month. Includes cancellations, downgrades, and contractions. May be larger than Controllable + Uncontrollable if some churn has no reason code assigned.",
        },
        {
            key: "total_churn_ctrl",
            label: "Churn (Controllable)",
            color: chartColor,
            format: formatCurrencyFull,
            showZeroLine: true,
            description:
                "Negative CARR adjustments with controllable reason codes: Product Issues, Service/Support, Pricing, Competition, Implementation Failure, or Internal Decision. These represent churn that could potentially be prevented.",
        },
        {
            key: "total_churn_unctrl",
            label: "Churn (Uncontrollable)",
            color: chartColor,
            format: formatCurrencyFull,
            showZeroLine: true,
            description:
                "Negative CARR adjustments with uncontrollable reason codes: Customer Out of Business, Acquired/Merged, Budget Cuts, Change in Strategy, or Force Majeure. These represent churn outside your control.",
        },
        {
            key: "yoy_carr_growth",
            label: "Year-over-Year CARR Growth Percentage",
            color: chartColor,
            format: formatPercent,
            showZeroLine: true,
            description:
                "Percentage change in Total CARR compared to the same month last year. Calculated as (Current Total CARR - Total CARR 12 months ago) / Total CARR 12 months ago.",
        },
        {
            key: "l3m_gross_churn",
            label: "Last 3 Months Gross Churn Percentage",
            color: chartColor,
            format: formatPercent,
            showZeroLine: true,
            description: `Rolling 3-month gross churn rate. Calculated as (Churn this month + Churn last month + Churn 2 months ago) / Total CARR at start of 3 month period.

Example for Dec:
          (Dec Churn + Nov Churn + Oct Churn)
        ──────────────────
                     Opening CARR (Oct 1)`,
        },
        {
            key: "grr",
            label: "Monthly Gross Revenue Retention (GRR)",
            color: chartColor,
            format: formatPercent,
            description:
                "Monthly Gross Revenue Retention. Calculated as (Opening CARR - Downsell - Churn) / Opening CARR, where Opening CARR is Total CARR at the start of the month and downsell/churn are the month's negative CARR adjustments. Blank when Opening CARR is zero (e.g. a product's first month).",
        },
        {
            key: "ttm_grr",
            label: "Trailing 12 Month Gross Revenue Retention (GRR)",
            color: chartColor,
            format: formatPercent,
            description:
                "Trailing 12 month Gross Revenue Retention. Calculated as (Opening CARR 12 months ago - Downsell and Churn over the last 12 months) / Opening CARR 12 months ago. Directly comparable to an annual retention target (e.g. GRR above 90%). Blank until 12 months of history exist.",
        },
        {
            key: "escalated_to_dev",
            label: "Support Tickets Escalated to Dev",
            color: "#ef4444",
            format: (v) => v?.toLocaleString() || "-",
            description:
                "Monthly count of support tickets that were escalated to development team.",
            useTicketData: true,
        },
        {
            key: "not_escalated",
            label: "Support Tickets Not Escalated to Dev",
            color: "#22c55e",
            format: (v) => v?.toLocaleString() || "-",
            description:
                "Monthly count of support tickets that were not escalated to development team.",
            useTicketData: true,
        },
        {
            key: "total_tickets",
            label: "Support Tickets Total",
            color: "#3b82f6",
            format: (v) => v?.toLocaleString() || "-",
            description: "Monthly count of all support tickets.",
            useTicketData: true,
        },
        {
            key: "close_rate",
            label: "Close Rate by Volume",
            color: "#8b5cf6",
            format: (v) => (v !== null && v !== undefined ? `${(v * 100).toFixed(1)}%` : "-"),
            description:
                "Monthly deal close rate. Calculated as Closed Won / (Closed Won + Closed Lost). Data from HubSpot.",
            useCloseRateData: true,
        },
        {
            key: "close_rate_by_amount",
            label: "Close Rate by Dollar",
            color: "#ec4899",
            format: (v) => (v !== null && v !== undefined ? `${(v * 100).toFixed(1)}%` : "-"),
            description:
                "Monthly deal close rate by dollar amount. Calculated as Won Amount / (Won Amount + Lost Amount). Data from HubSpot.",
            useCloseRateData: true,
        },
    ];

    // Show loading until selectors have data AND initial fetch has completed
    if (
        productOptions.length === 0 ||
        productFamilyOptions.length === 0 ||
        !initialFetchDone.current
    ) {
        return <LoadingOverlay text="Loading product health..." />;
    }

    // Show breakdown when filtering by family or business unit (not individual product)
    const showBreakdown =
        selectedProducts.length === 0 && (!!selectedProductFamily || !!selectedBusinessUnit);

    return (
        <Box sx={{ width: "100%", p: 2 }}>
            <Box
                sx={{
                    mb: 3,
                    display: "flex",
                    gap: 3,
                    position: "sticky",
                    top: 0,
                    bgcolor: "white",
                    zIndex: 200,
                }}
            >
                <MultiSelectWithApply
                    label="Product"
                    options={productOptions}
                    selected={draftProducts}
                    onChange={handleProductChange}
                    width={250}
                />
                <Box>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        Product Family
                    </label>
                    <Autocomplete
                        options={productFamilyOptions}
                        getOptionLabel={(option) => option.name || ""}
                        value={draftProductFamily}
                        onChange={handleProductFamilyChange}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderOption={(props, option) => {
                            const { key, ...rest } = props;
                            return (
                                <li key={key} {...rest} style={{ fontSize: "0.875rem" }}>
                                    {option.name}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder="Select"
                                sx={{
                                    "& .MuiInputBase-input": { fontSize: "0.875rem" },
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "0.375rem",
                                    },
                                }}
                            />
                        )}
                        sx={{
                            width: getMaxWidth(productFamilyOptions),
                        }}
                    />
                </Box>
                <Box>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                        Business Unit
                    </label>
                    <Autocomplete
                        options={businessUnitOptions}
                        getOptionLabel={(option) => option.name || ""}
                        value={draftBusinessUnit}
                        onChange={handleBusinessUnitChange}
                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                        renderOption={(props, option) => {
                            const { key, ...rest } = props;
                            return (
                                <li key={key} {...rest} style={{ fontSize: "0.875rem" }}>
                                    {option.name}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder="Select"
                                sx={{
                                    "& .MuiInputBase-input": { fontSize: "0.875rem" },
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "0.375rem",
                                    },
                                }}
                            />
                        )}
                        sx={{
                            width: getMaxWidth(businessUnitOptions),
                        }}
                    />
                </Box>
                <MonthRangePicker value={draftMonthRange} onChange={setDraftMonthRange} />
                <Box sx={{ display: "flex", alignItems: "flex-end" }}>
                    <Button
                        variant="contained"
                        onClick={handleApplyFilters}
                        sx={{ height: 40, textTransform: "none" }}
                    >
                        Apply
                    </Button>
                </Box>
            </Box>

            <Box sx={{ position: "relative" }}>
                {isLoading && <LoadingOverlay contained text="Loading product health..." />}
                <Grid container spacing={14}>
                    {metrics
                        .filter((m) => !m.useTicketData && !m.useCloseRateData)
                        .map((metric) => {
                            // For multi-product, pivot the data so each product is a column
                            let chartData;
                            let productNames = [];

                            // Ticket escalation uses its own data source
                            if (
                                metric.useTicketData &&
                                isMultiProductTicket &&
                                perProductTicketData.length > 0
                            ) {
                                // Multi-product ticket data - pivot by product
                                productNames = [
                                    ...new Set(perProductTicketData.map((d) => d.product_name)),
                                ];

                                const periods = [
                                    ...new Set(perProductTicketData.map((d) => d.period)),
                                ]
                                    .filter((p) => isPeriodInRange(p, appliedMonthRange))
                                    .sort((a, b) => new Date(a) - new Date(b));

                                chartData = periods.map((period) => {
                                    const row = {
                                        period,
                                        periodLabel: formatDate(period),
                                    };
                                    productNames.forEach((productName) => {
                                        const match = perProductTicketData.find(
                                            (d) =>
                                                d.period === period &&
                                                d.product_name === productName
                                        );
                                        row[productName] = match ? match[metric.key] : null;
                                    });
                                    return row;
                                });
                            } else if (metric.useTicketData) {
                                chartData = ticketEscalationData.filter((d) =>
                                    isPeriodInRange(d.period, appliedMonthRange)
                                );
                            } else if (metric.useCloseRateData) {
                                chartData = windowedCloseRateData;
                            } else if (isMultiProduct && perProductData.length > 0) {
                                // Get unique product names
                                productNames = [
                                    ...new Set(perProductData.map((d) => d.product_name)),
                                ];

                                // Get unique periods within the applied month range
                                const periods = [...new Set(perProductData.map((d) => d.period))]
                                    .filter((p) => isPeriodInRange(p, appliedMonthRange))
                                    .sort((a, b) => new Date(a) - new Date(b));

                                // Pivot data: each row has period + each product's value
                                chartData = periods.map((period) => {
                                    const row = {
                                        period,
                                        periodLabel: formatDate(period),
                                    };
                                    productNames.forEach((productName) => {
                                        const match = perProductData.find(
                                            (d) =>
                                                d.period === period &&
                                                d.product_name === productName
                                        );
                                        row[productName] = match ? match[metric.key] : null;
                                    });
                                    return row;
                                });
                            } else {
                                chartData = healthData.filter((d) =>
                                    isPeriodInRange(d.period, appliedMonthRange)
                                );
                            }

                            // Calculate max value for Y-axis scaling
                            let maxValue = 0;
                            if (metric.isMultiLine && metric.lines) {
                                metric.lines.forEach((line) => {
                                    const vals = chartData.map((d) => Math.abs(d[line.key] || 0));
                                    maxValue = Math.max(maxValue, ...vals);
                                });
                            } else if (
                                (isMultiProduct ||
                                    (metric.useTicketData && isMultiProductTicket)) &&
                                productNames.length > 0
                            ) {
                                // Multi-product data (either health or ticket data)
                                productNames.forEach((name) => {
                                    const vals = chartData.map((d) => Math.abs(d[name] || 0));
                                    maxValue = Math.max(maxValue, ...vals);
                                });
                            } else {
                                maxValue = Math.max(
                                    ...chartData.map((d) => Math.abs(d[metric.key] || 0))
                                );
                            }
                            const useMillions = maxValue >= 1000000;

                            // Show empty chart with message if no data
                            const hasNoData = !chartData || chartData.length === 0;

                            return (
                                <Grid item xs={12} key={metric.key}>
                                    <Paper sx={{ p: 2 }}>
                                        <h2
                                            style={{
                                                fontWeight: "bold",
                                                marginBottom: "16px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            {metric.label}
                                            <MuiTooltip
                                                title={
                                                    <span style={{ whiteSpace: "pre-wrap" }}>
                                                        {metric.description}
                                                    </span>
                                                }
                                                arrow
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: "rgba(97, 97, 97, 1)",
                                                            fontSize: "0.875rem",
                                                            maxWidth: 400,
                                                            "& .MuiTooltip-arrow": {
                                                                color: "rgba(97, 97, 97, 1)",
                                                            },
                                                        },
                                                    },
                                                }}
                                            >
                                                <IconButton size="small" sx={{ p: 0.5 }}>
                                                    <InfoOutlinedIcon
                                                        fontSize="small"
                                                        sx={{ color: "#888" }}
                                                    />
                                                </IconButton>
                                            </MuiTooltip>
                                        </h2>
                                        {hasNoData ? (
                                            <Box
                                                sx={{
                                                    height: 470,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "text.secondary",
                                                    bgcolor: "#fafafa",
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography variant="body1">
                                                    No data available for the selected filters
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={470}>
                                                <LineChart
                                                    data={chartData}
                                                    margin={{ top: 20, right: 30, bottom: 50 }}
                                                >
                                                    <CartesianGrid
                                                        strokeDasharray="3 3"
                                                        stroke="#eee"
                                                    />
                                                    <XAxis
                                                        dataKey="periodLabel"
                                                        angle={-45}
                                                        textAnchor="end"
                                                        height={60}
                                                        interval={0}
                                                        tick={{ style: { fontSize: 15 } }}
                                                    />
                                                    <YAxis
                                                        tickFormatter={(value) => {
                                                            if (
                                                                metric.key.includes("churn") &&
                                                                metric.key.includes("l3m")
                                                            ) {
                                                                return `${(value * 100).toFixed(0)}%`;
                                                            }
                                                            if (metric.key === "yoy_carr_growth") {
                                                                return `${(value * 100).toFixed(0)}%`;
                                                            }
                                                            if (
                                                                metric.key === "grr" ||
                                                                metric.key === "ttm_grr"
                                                            ) {
                                                                return `${(value * 100).toFixed(0)}%`;
                                                            }
                                                            if (
                                                                metric.key.includes("carr") ||
                                                                metric.key.includes("churn")
                                                            ) {
                                                                if (useMillions) {
                                                                    return `$${(value / 1000000).toFixed(1)}M`;
                                                                }
                                                                return `$${Math.round(value / 1000).toLocaleString()}K`;
                                                            }
                                                            return value.toLocaleString();
                                                        }}
                                                        width={80}
                                                        tick={{ style: { fontSize: 15 } }}
                                                    />
                                                    <Tooltip
                                                        content={({ active, payload, label }) => {
                                                            if (
                                                                !active ||
                                                                !payload ||
                                                                !payload.length
                                                            )
                                                                return null;
                                                            const dataPoint = payload[0].payload;
                                                            // Use keyed format for close rate, regular format for others
                                                            const breakdown =
                                                                metric.useCloseRateData
                                                                    ? dataPoint
                                                                          ?.product_breakdown_keyed?.[
                                                                          metric.key
                                                                      ]
                                                                    : dataPoint
                                                                          ?.product_breakdown?.[
                                                                          metric.key
                                                                      ];
                                                            const hasBreakdown =
                                                                showBreakdown &&
                                                                breakdownMetrics.includes(
                                                                    metric.key
                                                                ) &&
                                                                breakdown &&
                                                                breakdown.length > 0;
                                                            return (
                                                                <Paper
                                                                    sx={{
                                                                        p: 1,
                                                                        bgcolor:
                                                                            "rgba(255,255,255,0.95)",
                                                                        maxWidth: 300,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="bold"
                                                                        sx={{ mb: 0.5 }}
                                                                    >
                                                                        {label}
                                                                    </Typography>
                                                                    {isMultiProduct ||
                                                                    metric.isMultiLine ||
                                                                    (metric.useTicketData &&
                                                                        isMultiProductTicket) ? (
                                                                        payload
                                                                            .filter(
                                                                                (entry) =>
                                                                                    entry.value !=
                                                                                    null
                                                                            )
                                                                            .map((entry, idx) => (
                                                                                <Typography
                                                                                    key={idx}
                                                                                    variant="body2"
                                                                                >
                                                                                    <span
                                                                                        style={{
                                                                                            display:
                                                                                                "inline-block",
                                                                                            width: 8,
                                                                                            height: 8,
                                                                                            backgroundColor:
                                                                                                entry.color,
                                                                                            borderRadius:
                                                                                                "50%",
                                                                                            marginRight: 6,
                                                                                        }}
                                                                                    />
                                                                                    {entry.name}:{" "}
                                                                                    {metric.format(
                                                                                        entry.value
                                                                                    )}
                                                                                </Typography>
                                                                            ))
                                                                    ) : (
                                                                        <Typography variant="body2">
                                                                            {metric.format(
                                                                                payload[0].value
                                                                            )}
                                                                        </Typography>
                                                                    )}
                                                                    {hasBreakdown && (
                                                                        <Box
                                                                            sx={{
                                                                                mt: 1,
                                                                                pt: 1,
                                                                                borderTop:
                                                                                    "1px solid #eee",
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="caption"
                                                                                fontWeight="bold"
                                                                                sx={{
                                                                                    display:
                                                                                        "block",
                                                                                    mb: 0.5,
                                                                                }}
                                                                            >
                                                                                Product Breakdown:
                                                                            </Typography>
                                                                            {[...breakdown]
                                                                                .sort((a, b) =>
                                                                                    (
                                                                                        a.product_name ||
                                                                                        a.netsuite_product_name ||
                                                                                        ""
                                                                                    ).localeCompare(
                                                                                        b.product_name ||
                                                                                            b.netsuite_product_name ||
                                                                                            ""
                                                                                    )
                                                                                )
                                                                                .map(
                                                                                    (item, idx) => (
                                                                                        <Typography
                                                                                            key={
                                                                                                idx
                                                                                            }
                                                                                            variant="caption"
                                                                                            sx={{
                                                                                                display:
                                                                                                    "block",
                                                                                            }}
                                                                                        >
                                                                                            {item.product_name ||
                                                                                                item.netsuite_product_name}
                                                                                            :{" "}
                                                                                            {metric.format(
                                                                                                item.value
                                                                                            )}
                                                                                        </Typography>
                                                                                    )
                                                                                )}
                                                                        </Box>
                                                                    )}
                                                                </Paper>
                                                            );
                                                        }}
                                                    />
                                                    {metric.showZeroLine && (
                                                        <ReferenceLine
                                                            y={0}
                                                            stroke="#888"
                                                            strokeDasharray="3 3"
                                                        />
                                                    )}
                                                    {metric.isMultiLine && metric.lines ? (
                                                        <>
                                                            {metric.lines.map((line) => (
                                                                <Line
                                                                    key={line.key}
                                                                    type="linear"
                                                                    dataKey={line.key}
                                                                    name={line.label}
                                                                    stroke={line.color}
                                                                    strokeWidth={2}
                                                                    dot={{ r: 3 }}
                                                                    activeDot={{ r: 5 }}
                                                                    connectNulls={true}
                                                                />
                                                            ))}
                                                            <Legend />
                                                        </>
                                                    ) : (isMultiProduct ||
                                                          (metric.useTicketData &&
                                                              isMultiProductTicket)) &&
                                                      productNames.length > 0 ? (
                                                        <>
                                                            {productNames.map(
                                                                (productName, idx) => (
                                                                    <Line
                                                                        key={productName}
                                                                        type="linear"
                                                                        dataKey={productName}
                                                                        name={productName}
                                                                        stroke={
                                                                            PRODUCT_COLORS[
                                                                                idx %
                                                                                    PRODUCT_COLORS.length
                                                                            ]
                                                                        }
                                                                        strokeWidth={2}
                                                                        dot={{ r: 3 }}
                                                                        activeDot={{ r: 5 }}
                                                                        connectNulls={true}
                                                                    />
                                                                )
                                                            )}
                                                            <Legend />
                                                        </>
                                                    ) : (
                                                        <Line
                                                            type="linear"
                                                            dataKey={metric.key}
                                                            stroke={metric.color}
                                                            strokeWidth={2}
                                                            dot={{ r: 3 }}
                                                            activeDot={{ r: 5 }}
                                                            connectNulls={true}
                                                        />
                                                    )}
                                                </LineChart>
                                            </ResponsiveContainer>
                                        )}
                                    </Paper>
                                </Grid>
                            );
                        })}
                </Grid>

                <Grid container spacing={14} sx={{ mt: 6 }}>
                    {metrics
                        .filter((m) => m.useTicketData)
                        .map((metric) => {
                            // For multi-product, pivot the data so each product is a column
                            let chartData;
                            let productNames = [];

                            // Ticket escalation uses its own data source
                            if (isMultiProductTicket && perProductTicketData.length > 0) {
                                // Multi-product ticket data - pivot by product
                                productNames = [
                                    ...new Set(perProductTicketData.map((d) => d.product_name)),
                                ];

                                const periods = [
                                    ...new Set(perProductTicketData.map((d) => d.period)),
                                ]
                                    .filter((p) => isPeriodInRange(p, appliedMonthRange))
                                    .sort((a, b) => new Date(a) - new Date(b));

                                chartData = periods.map((period) => {
                                    const row = {
                                        period,
                                        periodLabel: formatDate(period),
                                    };
                                    productNames.forEach((productName) => {
                                        const match = perProductTicketData.find(
                                            (d) =>
                                                d.period === period &&
                                                d.product_name === productName
                                        );
                                        row[productName] = match ? match[metric.key] : null;
                                    });
                                    return row;
                                });
                            } else {
                                chartData = ticketEscalationData.filter((d) =>
                                    isPeriodInRange(d.period, appliedMonthRange)
                                );
                            }

                            // Show empty chart with message if no data
                            const hasNoData = !chartData || chartData.length === 0;

                            // Calculate max value for Y-axis scaling
                            let maxValue = 0;
                            if (!hasNoData) {
                                if (isMultiProductTicket && productNames.length > 0) {
                                    productNames.forEach((name) => {
                                        const vals = chartData.map((d) => Math.abs(d[name] || 0));
                                        maxValue = Math.max(maxValue, ...vals);
                                    });
                                } else {
                                    maxValue = Math.max(
                                        ...chartData.map((d) => Math.abs(d[metric.key] || 0))
                                    );
                                }
                            }

                            return (
                                <Grid item xs={12} key={metric.key}>
                                    <Paper sx={{ p: 2 }}>
                                        <h2
                                            style={{
                                                fontWeight: "bold",
                                                marginBottom: "16px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            {metric.label}
                                            <MuiTooltip
                                                title={
                                                    <span style={{ whiteSpace: "pre-wrap" }}>
                                                        {metric.description}
                                                    </span>
                                                }
                                                arrow
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: "rgba(97, 97, 97, 1)",
                                                            fontSize: "0.875rem",
                                                            maxWidth: 400,
                                                            "& .MuiTooltip-arrow": {
                                                                color: "rgba(97, 97, 97, 1)",
                                                            },
                                                        },
                                                    },
                                                }}
                                            >
                                                <IconButton size="small" sx={{ p: 0.5 }}>
                                                    <InfoOutlinedIcon
                                                        fontSize="small"
                                                        sx={{ color: "#888" }}
                                                    />
                                                </IconButton>
                                            </MuiTooltip>
                                        </h2>
                                        {hasNoData ? (
                                            <Box
                                                sx={{
                                                    height: 470,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "text.secondary",
                                                    bgcolor: "#fafafa",
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography variant="body1">
                                                    No data available for the selected filters
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={470}>
                                                <LineChart
                                                    data={chartData}
                                                    margin={{ top: 20, right: 30, bottom: 50 }}
                                                >
                                                    <CartesianGrid
                                                        strokeDasharray="3 3"
                                                        stroke="#eee"
                                                    />
                                                    <XAxis
                                                        dataKey="periodLabel"
                                                        angle={-45}
                                                        textAnchor="end"
                                                        height={60}
                                                        interval={0}
                                                        tick={{ style: { fontSize: 15 } }}
                                                    />
                                                    <YAxis
                                                        tickFormatter={(value) =>
                                                            value.toLocaleString()
                                                        }
                                                        tick={{ style: { fontSize: 15 } }}
                                                    />
                                                    <Tooltip
                                                        content={({ active, payload, label }) => {
                                                            if (!active || !payload?.length)
                                                                return null;
                                                            const dataPoint = payload[0].payload;
                                                            const breakdown =
                                                                dataPoint?.product_breakdown?.[
                                                                    metric.key
                                                                ];
                                                            const hasBreakdown =
                                                                showBreakdown &&
                                                                breakdownMetrics.includes(
                                                                    metric.key
                                                                ) &&
                                                                breakdown &&
                                                                breakdown.length > 0;
                                                            return (
                                                                <Paper
                                                                    sx={{
                                                                        p: 1,
                                                                        bgcolor:
                                                                            "rgba(255,255,255,0.95)",
                                                                        maxWidth: 300,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="bold"
                                                                        sx={{ mb: 0.5 }}
                                                                    >
                                                                        {label}
                                                                    </Typography>
                                                                    {isMultiProductTicket ? (
                                                                        payload
                                                                            .filter(
                                                                                (entry) =>
                                                                                    entry.value !=
                                                                                    null
                                                                            )
                                                                            .map((entry, idx) => (
                                                                                <Typography
                                                                                    key={idx}
                                                                                    variant="body2"
                                                                                >
                                                                                    <span
                                                                                        style={{
                                                                                            display:
                                                                                                "inline-block",
                                                                                            width: 8,
                                                                                            height: 8,
                                                                                            backgroundColor:
                                                                                                entry.color,
                                                                                            borderRadius:
                                                                                                "50%",
                                                                                            marginRight: 6,
                                                                                        }}
                                                                                    />
                                                                                    {entry.name}:{" "}
                                                                                    {metric.format(
                                                                                        entry.value
                                                                                    )}
                                                                                </Typography>
                                                                            ))
                                                                    ) : (
                                                                        <Typography variant="body2">
                                                                            {metric.format(
                                                                                payload[0].value
                                                                            )}
                                                                        </Typography>
                                                                    )}
                                                                    {hasBreakdown && (
                                                                        <Box
                                                                            sx={{
                                                                                mt: 1,
                                                                                pt: 1,
                                                                                borderTop:
                                                                                    "1px solid #eee",
                                                                            }}
                                                                        >
                                                                            <Typography
                                                                                variant="caption"
                                                                                fontWeight="bold"
                                                                                sx={{
                                                                                    display:
                                                                                        "block",
                                                                                    mb: 0.5,
                                                                                }}
                                                                            >
                                                                                Product Breakdown:
                                                                            </Typography>
                                                                            {[...breakdown]
                                                                                .sort((a, b) =>
                                                                                    (
                                                                                        a.product_name ||
                                                                                        ""
                                                                                    ).localeCompare(
                                                                                        b.product_name ||
                                                                                            ""
                                                                                    )
                                                                                )
                                                                                .map(
                                                                                    (item, idx) => (
                                                                                        <Typography
                                                                                            key={
                                                                                                idx
                                                                                            }
                                                                                            variant="caption"
                                                                                            sx={{
                                                                                                display:
                                                                                                    "block",
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                item.product_name
                                                                                            }
                                                                                            :{" "}
                                                                                            {metric.format(
                                                                                                item.value
                                                                                            )}
                                                                                        </Typography>
                                                                                    )
                                                                                )}
                                                                        </Box>
                                                                    )}
                                                                </Paper>
                                                            );
                                                        }}
                                                    />
                                                    {isMultiProductTicket &&
                                                    productNames.length > 0 ? (
                                                        <>
                                                            {productNames.map(
                                                                (productName, idx) => (
                                                                    <Line
                                                                        key={productName}
                                                                        type="linear"
                                                                        dataKey={productName}
                                                                        name={productName}
                                                                        stroke={
                                                                            PRODUCT_COLORS[
                                                                                idx %
                                                                                    PRODUCT_COLORS.length
                                                                            ]
                                                                        }
                                                                        strokeWidth={2}
                                                                        dot={{ r: 3 }}
                                                                        activeDot={{ r: 5 }}
                                                                        connectNulls={true}
                                                                    />
                                                                )
                                                            )}
                                                            <Legend />
                                                        </>
                                                    ) : (
                                                        <Line
                                                            type="linear"
                                                            dataKey={metric.key}
                                                            stroke={metric.color}
                                                            strokeWidth={2}
                                                            dot={{ r: 3 }}
                                                            activeDot={{ r: 5 }}
                                                            connectNulls={true}
                                                        />
                                                    )}
                                                </LineChart>
                                            </ResponsiveContainer>
                                        )}
                                    </Paper>
                                </Grid>
                            );
                        })}
                </Grid>

                {/* Close Rate Section */}
                <Grid container spacing={14} sx={{ mt: 6 }}>
                    {metrics
                        .filter((m) => m.useCloseRateData)
                        .map((metric) => {
                            // Determine which products to filter by based on selected filters
                            const isMultiProductCloseRate = selectedProducts.length > 1;
                            let chartData;
                            let closeRateProductNames = [];

                            // Get products to filter by (based on product, family, or business unit selection)
                            const getFilteredProductNames = () => {
                                if (selectedProducts.length > 0) {
                                    return selectedProducts.map((p) => p.name.toLowerCase());
                                }
                                if (selectedProductFamily) {
                                    // Get all products in the selected family
                                    return productOptions
                                        .filter(
                                            (p) =>
                                                String(p.custrecord_ng_prd_org_grouping) ===
                                                String(selectedProductFamily.id)
                                        )
                                        .map((p) => p.name.toLowerCase());
                                }
                                if (selectedBusinessUnit) {
                                    // Get all products in the selected business unit
                                    return productOptions
                                        .filter(
                                            (p) =>
                                                String(p.custrecord_ng_prd_business_unit) ===
                                                String(selectedBusinessUnit.id)
                                        )
                                        .map((p) => p.name.toLowerCase());
                                }
                                return null; // No filter - show overall
                            };

                            const filteredProductNames = getFilteredProductNames();
                            const hasFilter = filteredProductNames !== null;

                            // Helper to aggregate product breakdown data for filtered products
                            const aggregateProductBreakdown = (
                                breakdown,
                                productNamesToInclude
                            ) => {
                                const filtered = (breakdown || []).filter((p) => {
                                    const name = (
                                        p.product_name || p.netsuite_product_name
                                    )?.toLowerCase();
                                    return productNamesToInclude.includes(name);
                                });
                                if (filtered.length === 0) return null;

                                const totals = filtered.reduce(
                                    (acc, p) => ({
                                        closed_won: acc.closed_won + (p.closed_won || 0),
                                        closed_lost: acc.closed_lost + (p.closed_lost || 0),
                                        amount_won: acc.amount_won + (p.amount_won || 0),
                                        amount_lost: acc.amount_lost + (p.amount_lost || 0),
                                    }),
                                    { closed_won: 0, closed_lost: 0, amount_won: 0, amount_lost: 0 }
                                );

                                const totalCount = totals.closed_won + totals.closed_lost;
                                const totalAmount = totals.amount_won + totals.amount_lost;

                                return {
                                    ...totals,
                                    total: totalCount,
                                    total_amount: totalAmount,
                                    close_rate:
                                        totalCount > 0 ? totals.closed_won / totalCount : null,
                                    close_rate_by_amount:
                                        totalAmount > 0 ? totals.amount_won / totalAmount : null,
                                };
                            };

                            if (isMultiProductCloseRate && closeRateData.length > 0) {
                                // Multi-product view: show separate line for each product
                                closeRateProductNames = selectedProducts.map((p) => p.name);

                                chartData = windowedCloseRateData.map((row) => {
                                    const pivotedRow = {
                                        period: row.period,
                                        periodLabel: row.periodLabel,
                                        _productCounts: {},
                                    };
                                    selectedProducts.forEach((product) => {
                                        const productData = (row.product_breakdown || []).find(
                                            (p) =>
                                                (
                                                    p.product_name || p.netsuite_product_name
                                                )?.toLowerCase() === product.name?.toLowerCase() ||
                                                String(p.netsuite_product_id) === String(product.id)
                                        );
                                        if (productData) {
                                            pivotedRow[product.name] = productData[metric.key];
                                            pivotedRow._productCounts[product.name] = {
                                                closed_won: productData.closed_won,
                                                closed_lost: productData.closed_lost,
                                                total: productData.total,
                                                amount_won: productData.amount_won,
                                                amount_lost: productData.amount_lost,
                                                total_amount: productData.total_amount,
                                            };
                                        } else {
                                            pivotedRow[product.name] = null;
                                        }
                                    });
                                    return pivotedRow;
                                });
                            } else if (hasFilter && closeRateData.length > 0) {
                                // Single product, family, or business unit filter: aggregate and show single line
                                chartData = windowedCloseRateData.map((row) => {
                                    const aggregated = aggregateProductBreakdown(
                                        row.product_breakdown,
                                        filteredProductNames
                                    );
                                    if (aggregated) {
                                        return {
                                            period: row.period,
                                            periodLabel: row.periodLabel,
                                            closed_won: aggregated.closed_won,
                                            closed_lost: aggregated.closed_lost,
                                            total_closed: aggregated.total,
                                            close_rate: aggregated.close_rate,
                                            amount_won: aggregated.amount_won,
                                            amount_lost: aggregated.amount_lost,
                                            total_amount: aggregated.total_amount,
                                            close_rate_by_amount: aggregated.close_rate_by_amount,
                                            product_breakdown_keyed: row.product_breakdown_keyed,
                                        };
                                    }
                                    return {
                                        period: row.period,
                                        periodLabel: row.periodLabel,
                                        closed_won: 0,
                                        closed_lost: 0,
                                        total_closed: 0,
                                        close_rate: null,
                                        amount_won: 0,
                                        amount_lost: 0,
                                        total_amount: 0,
                                        close_rate_by_amount: null,
                                        product_breakdown_keyed: row.product_breakdown_keyed,
                                    };
                                });
                            } else {
                                // No filter - show overall close rate
                                chartData = windowedCloseRateData;
                            }

                            const hasNoData = !chartData || chartData.length === 0;

                            // Calculate max value for Y-axis scaling (percentage, max 100%)
                            let maxValue = 1; // Default to 100% for percentage charts

                            return (
                                <Grid item xs={12} key={metric.key}>
                                    <Paper sx={{ p: 2 }}>
                                        <h2
                                            style={{
                                                fontWeight: "bold",
                                                marginBottom: "16px",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "8px",
                                            }}
                                        >
                                            {metric.label}
                                            <MuiTooltip
                                                title={
                                                    <span style={{ whiteSpace: "pre-wrap" }}>
                                                        {metric.description}
                                                    </span>
                                                }
                                                arrow
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: "rgba(97, 97, 97, 1)",
                                                            fontSize: "0.875rem",
                                                            maxWidth: 400,
                                                            "& .MuiTooltip-arrow": {
                                                                color: "rgba(97, 97, 97, 1)",
                                                            },
                                                        },
                                                    },
                                                }}
                                            >
                                                <IconButton size="small" sx={{ p: 0.5 }}>
                                                    <InfoOutlinedIcon
                                                        fontSize="small"
                                                        sx={{ color: "#888" }}
                                                    />
                                                </IconButton>
                                            </MuiTooltip>
                                        </h2>
                                        {hasNoData ? (
                                            <Box
                                                sx={{
                                                    height: 470,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "text.secondary",
                                                    bgcolor: "#fafafa",
                                                    borderRadius: 1,
                                                }}
                                            >
                                                <Typography variant="body1">
                                                    No data available
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <ResponsiveContainer width="100%" height={470}>
                                                <LineChart
                                                    data={chartData}
                                                    margin={{ top: 20, right: 30, bottom: 50 }}
                                                >
                                                    <CartesianGrid
                                                        strokeDasharray="3 3"
                                                        stroke="#eee"
                                                    />
                                                    <XAxis
                                                        dataKey="periodLabel"
                                                        angle={-45}
                                                        textAnchor="end"
                                                        height={60}
                                                        interval={0}
                                                        tick={{ style: { fontSize: 15 } }}
                                                    />
                                                    <YAxis
                                                        domain={[0, maxValue]}
                                                        tickFormatter={(value) =>
                                                            `${(value * 100).toFixed(0)}%`
                                                        }
                                                        tick={{ style: { fontSize: 15 } }}
                                                    />
                                                    <Tooltip
                                                        content={({ active, payload, label }) => {
                                                            if (!active || !payload?.length)
                                                                return null;
                                                            const data = payload[0].payload;
                                                            return (
                                                                <Box
                                                                    sx={{
                                                                        bgcolor: "white",
                                                                        border: "1px solid #ccc",
                                                                        borderRadius: 1,
                                                                        p: 1,
                                                                        boxShadow: 1,
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        fontWeight="bold"
                                                                        sx={{ mb: 0.5 }}
                                                                    >
                                                                        {label}
                                                                    </Typography>
                                                                    {isMultiProductCloseRate ? (
                                                                        payload
                                                                            .filter(
                                                                                (entry) =>
                                                                                    entry.value !=
                                                                                    null
                                                                            )
                                                                            .map((entry, idx) => {
                                                                                const counts =
                                                                                    data
                                                                                        ._productCounts?.[
                                                                                        entry.name
                                                                                    ];
                                                                                return (
                                                                                    <Box
                                                                                        key={idx}
                                                                                        sx={{
                                                                                            mb:
                                                                                                idx <
                                                                                                payload.length -
                                                                                                    1
                                                                                                    ? 0.5
                                                                                                    : 0,
                                                                                        }}
                                                                                    >
                                                                                        <Typography variant="body2">
                                                                                            <span
                                                                                                style={{
                                                                                                    display:
                                                                                                        "inline-block",
                                                                                                    width: 8,
                                                                                                    height: 8,
                                                                                                    backgroundColor:
                                                                                                        entry.color,
                                                                                                    borderRadius:
                                                                                                        "50%",
                                                                                                    marginRight: 6,
                                                                                                }}
                                                                                            />
                                                                                            {
                                                                                                entry.name
                                                                                            }
                                                                                            :{" "}
                                                                                            {metric.format(
                                                                                                entry.value
                                                                                            )}
                                                                                        </Typography>
                                                                                        {counts && (
                                                                                            <Typography
                                                                                                variant="caption"
                                                                                                color="text.secondary"
                                                                                                sx={{
                                                                                                    pl: 1.75,
                                                                                                }}
                                                                                            >
                                                                                                {metric.key ===
                                                                                                "close_rate_by_amount" ? (
                                                                                                    <>
                                                                                                        Won:
                                                                                                        $
                                                                                                        {counts.amount_won?.toLocaleString() ||
                                                                                                            0}{" "}
                                                                                                        |
                                                                                                        Lost:
                                                                                                        $
                                                                                                        {counts.amount_lost?.toLocaleString() ||
                                                                                                            0}{" "}
                                                                                                        |
                                                                                                        Total:
                                                                                                        $
                                                                                                        {counts.total_amount?.toLocaleString() ||
                                                                                                            0}
                                                                                                    </>
                                                                                                ) : (
                                                                                                    <>
                                                                                                        Won:{" "}
                                                                                                        {
                                                                                                            counts.closed_won
                                                                                                        }{" "}
                                                                                                        |
                                                                                                        Lost:{" "}
                                                                                                        {
                                                                                                            counts.closed_lost
                                                                                                        }{" "}
                                                                                                        |
                                                                                                        Total:{" "}
                                                                                                        {
                                                                                                            counts.total
                                                                                                        }
                                                                                                    </>
                                                                                                )}
                                                                                            </Typography>
                                                                                        )}
                                                                                    </Box>
                                                                                );
                                                                            })
                                                                    ) : (
                                                                        <>
                                                                            <Typography variant="body2">
                                                                                {metric.format(
                                                                                    payload[0].value
                                                                                )}
                                                                            </Typography>
                                                                            <Typography
                                                                                variant="caption"
                                                                                color="text.secondary"
                                                                            >
                                                                                {metric.key ===
                                                                                "close_rate_by_amount" ? (
                                                                                    <>
                                                                                        Won: $
                                                                                        {data.amount_won?.toLocaleString() ||
                                                                                            0}{" "}
                                                                                        | Lost: $
                                                                                        {data.amount_lost?.toLocaleString() ||
                                                                                            0}{" "}
                                                                                        | Total: $
                                                                                        {data.total_amount?.toLocaleString() ||
                                                                                            0}
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        Won:{" "}
                                                                                        {
                                                                                            data.closed_won
                                                                                        }{" "}
                                                                                        | Lost:{" "}
                                                                                        {
                                                                                            data.closed_lost
                                                                                        }{" "}
                                                                                        | Total:{" "}
                                                                                        {
                                                                                            data.total_closed
                                                                                        }
                                                                                    </>
                                                                                )}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    {(() => {
                                                                        const breakdown =
                                                                            data
                                                                                ?.product_breakdown_keyed?.[
                                                                                metric.key
                                                                            ];
                                                                        const hasBreakdown =
                                                                            showBreakdown &&
                                                                            breakdownMetrics.includes(
                                                                                metric.key
                                                                            ) &&
                                                                            breakdown &&
                                                                            breakdown.length > 0;
                                                                        if (!hasBreakdown)
                                                                            return null;
                                                                        return (
                                                                            <Box
                                                                                sx={{
                                                                                    mt: 1,
                                                                                    pt: 1,
                                                                                    borderTop:
                                                                                        "1px solid #eee",
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    fontWeight="bold"
                                                                                    sx={{
                                                                                        display:
                                                                                            "block",
                                                                                        mb: 0.5,
                                                                                    }}
                                                                                >
                                                                                    Product
                                                                                    Breakdown:
                                                                                </Typography>
                                                                                {[...breakdown]
                                                                                    .sort((a, b) =>
                                                                                        (
                                                                                            a.product_name ||
                                                                                            ""
                                                                                        ).localeCompare(
                                                                                            b.product_name ||
                                                                                                ""
                                                                                        )
                                                                                    )
                                                                                    .map(
                                                                                        (
                                                                                            item,
                                                                                            idx
                                                                                        ) => (
                                                                                            <Typography
                                                                                                key={
                                                                                                    idx
                                                                                                }
                                                                                                variant="caption"
                                                                                                sx={{
                                                                                                    display:
                                                                                                        "block",
                                                                                                }}
                                                                                            >
                                                                                                {
                                                                                                    item.product_name
                                                                                                }
                                                                                                :{" "}
                                                                                                {metric.format(
                                                                                                    item.value
                                                                                                )}
                                                                                            </Typography>
                                                                                        )
                                                                                    )}
                                                                            </Box>
                                                                        );
                                                                    })()}
                                                                </Box>
                                                            );
                                                        }}
                                                    />
                                                    {isMultiProductCloseRate &&
                                                    closeRateProductNames.length > 0 ? (
                                                        <>
                                                            {closeRateProductNames.map(
                                                                (productName, idx) => (
                                                                    <Line
                                                                        key={productName}
                                                                        type="linear"
                                                                        dataKey={productName}
                                                                        name={productName}
                                                                        stroke={
                                                                            PRODUCT_COLORS[
                                                                                idx %
                                                                                    PRODUCT_COLORS.length
                                                                            ]
                                                                        }
                                                                        strokeWidth={2}
                                                                        dot={{ r: 3 }}
                                                                        activeDot={{ r: 5 }}
                                                                        connectNulls={true}
                                                                    />
                                                                )
                                                            )}
                                                            <Legend />
                                                        </>
                                                    ) : (
                                                        <Line
                                                            type="linear"
                                                            dataKey={metric.key}
                                                            stroke={metric.color}
                                                            strokeWidth={2}
                                                            dot={{ r: 3 }}
                                                            activeDot={{ r: 5 }}
                                                            connectNulls={true}
                                                        />
                                                    )}
                                                </LineChart>
                                            </ResponsiveContainer>
                                        )}
                                    </Paper>
                                </Grid>
                            );
                        })}
                </Grid>

                {/* Cohort Based Churn */}
                {windowedCohortData.length > 0 && (
                    <Grid container spacing={14} sx={{ mt: 6 }}>
                        <Grid item xs={12}>
                            <Paper sx={{ p: 2 }}>
                                <h2
                                    style={{
                                        fontWeight: "bold",
                                        marginBottom: "1rem",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    Cohort Based Churn
                                    <MuiTooltip
                                        title="Cohort retention: % of customers still active, by months since signup"
                                        arrow
                                        componentsProps={{
                                            tooltip: {
                                                sx: {
                                                    bgcolor: "rgba(97, 97, 97, 1)",
                                                    fontSize: "0.875rem",
                                                    maxWidth: 400,
                                                    "& .MuiTooltip-arrow": {
                                                        color: "rgba(97, 97, 97, 1)",
                                                    },
                                                },
                                            },
                                        }}
                                    >
                                        <IconButton size="small" sx={{ p: 0.5 }}>
                                            <InfoOutlinedIcon
                                                fontSize="small"
                                                sx={{ color: "#888" }}
                                            />
                                        </IconButton>
                                    </MuiTooltip>
                                </h2>
                                <Box sx={{ mt: "1rem" }}>
                                    <table
                                        style={{
                                            borderCollapse: "collapse",
                                            fontSize: "0.875rem",
                                            fontFamily: "inherit",
                                            width: "100%",
                                            maxWidth: "1600px",
                                        }}
                                    >
                                        <tbody>
                                            {(() => {
                                                const cohortMap = {};
                                                windowedCohortData.forEach((row) => {
                                                    if (!row.cohort) return;
                                                    if (!cohortMap[row.cohort]) {
                                                        cohortMap[row.cohort] = {
                                                            size: row.cohort_size,
                                                            months: {},
                                                        };
                                                    }
                                                    cohortMap[row.cohort].months[
                                                        row.months_since_start
                                                    ] = {
                                                        count: row.customers_retained,
                                                        rate: row.retention_rate,
                                                    };
                                                });
                                                const sortedCohorts = Object.keys(cohortMap)
                                                    .filter((k) => k)
                                                    .sort((a, b) => a.localeCompare(b));
                                                const colorStops = [
                                                    { rate: 100, color: [0, 100, 53] },
                                                    { rate: 88, color: [98, 175, 97] },
                                                    { rate: 78, color: [186, 212, 123] },
                                                    { rate: 72, color: [225, 231, 169] },
                                                    { rate: 68, color: [235, 227, 162] },
                                                    { rate: 66, color: [233, 215, 149] },
                                                    { rate: 64, color: [233, 210, 130] },
                                                    { rate: 63, color: [230, 201, 131] },
                                                    { rate: 62, color: [231, 191, 119] },
                                                    { rate: 61, color: [233, 187, 109] },
                                                    { rate: 60, color: [236, 176, 104] },
                                                ];
                                                const getColor = (data, month) => {
                                                    if (
                                                        !data ||
                                                        data.rate === null ||
                                                        data.rate === undefined
                                                    )
                                                        return { bg: "transparent", text: "#333" };
                                                    if (month === 0)
                                                        return { bg: "#006435", text: "white" };
                                                    const rate = data.rate;
                                                    for (
                                                        let i = 0;
                                                        i < colorStops.length - 1;
                                                        i++
                                                    ) {
                                                        if (
                                                            rate <= colorStops[i].rate &&
                                                            rate >= colorStops[i + 1].rate
                                                        ) {
                                                            const upper = colorStops[i];
                                                            const lower = colorStops[i + 1];
                                                            const t =
                                                                (rate - lower.rate) /
                                                                (upper.rate - lower.rate);
                                                            const r = Math.round(
                                                                lower.color[0] +
                                                                    t *
                                                                        (upper.color[0] -
                                                                            lower.color[0])
                                                            );
                                                            const g = Math.round(
                                                                lower.color[1] +
                                                                    t *
                                                                        (upper.color[1] -
                                                                            lower.color[1])
                                                            );
                                                            const b = Math.round(
                                                                lower.color[2] +
                                                                    t *
                                                                        (upper.color[2] -
                                                                            lower.color[2])
                                                            );
                                                            const luminance =
                                                                (0.299 * r +
                                                                    0.587 * g +
                                                                    0.114 * b) /
                                                                255;
                                                            return {
                                                                bg: `rgb(${r}, ${g}, ${b})`,
                                                                text:
                                                                    luminance > 0.5
                                                                        ? "#333"
                                                                        : "white",
                                                            };
                                                        }
                                                    }
                                                    return rate >= 100
                                                        ? { bg: "#006435", text: "white" }
                                                        : { bg: "#ECB068", text: "#333" };
                                                };
                                                // Calculate max months from the data
                                                const maxMonths = Math.max(
                                                    0,
                                                    ...Object.values(cohortMap).flatMap((c) =>
                                                        Object.keys(c.months).map(Number)
                                                    )
                                                );
                                                return [...Array(maxMonths + 1).keys()].map(
                                                    (month) => (
                                                        <tr key={month}>
                                                            <td
                                                                style={{
                                                                    padding: "4px 12px",
                                                                    fontWeight: 500,
                                                                    fontSize: "15px",
                                                                    border: "none",
                                                                    textAlign: "right",
                                                                    position: "sticky",
                                                                    left: 0,
                                                                    backgroundColor: "white",
                                                                    zIndex: 1,
                                                                }}
                                                            >
                                                                M{month}
                                                            </td>
                                                            {sortedCohorts.map((cohort) => {
                                                                const monthData =
                                                                    cohortMap[cohort].months[month];
                                                                const colors = getColor(
                                                                    monthData,
                                                                    month
                                                                );
                                                                const formatCohortLabel = (c) => {
                                                                    const [year, m] = c.split("-");
                                                                    const date = new Date(
                                                                        year,
                                                                        parseInt(m) - 1
                                                                    );
                                                                    return date.toLocaleDateString(
                                                                        "en-US",
                                                                        {
                                                                            month: "short",
                                                                            year: "numeric",
                                                                        }
                                                                    );
                                                                };
                                                                const tooltipContent = monthData ? (
                                                                    <Box sx={{ p: 0.5 }}>
                                                                        <div
                                                                            style={{
                                                                                fontWeight: 600,
                                                                                marginBottom: 4,
                                                                            }}
                                                                        >
                                                                            {formatCohortLabel(
                                                                                cohort
                                                                            )}{" "}
                                                                            Cohort
                                                                        </div>
                                                                        <div>
                                                                            <strong>Period:</strong>{" "}
                                                                            M{month} (
                                                                            {month === 0
                                                                                ? "signup month"
                                                                                : `${month} month${month > 1 ? "s" : ""} later`}
                                                                            )
                                                                        </div>
                                                                        <div>
                                                                            <strong>
                                                                                Retention:
                                                                            </strong>{" "}
                                                                            {Math.round(
                                                                                monthData.rate
                                                                            )}
                                                                            %
                                                                        </div>
                                                                        <div>
                                                                            <strong>
                                                                                Customers:
                                                                            </strong>{" "}
                                                                            {monthData.count.toLocaleString()}
                                                                        </div>
                                                                    </Box>
                                                                ) : null;
                                                                return (
                                                                    <MuiTooltip
                                                                        key={cohort}
                                                                        title={tooltipContent || ""}
                                                                        arrow
                                                                        placement="top"
                                                                        componentsProps={{
                                                                            tooltip: {
                                                                                sx: {
                                                                                    bgcolor:
                                                                                        "rgba(97, 97, 97, 0.95)",
                                                                                    fontSize:
                                                                                        "0.8rem",
                                                                                    "& .MuiTooltip-arrow":
                                                                                        {
                                                                                            color: "rgba(97, 97, 97, 0.95)",
                                                                                        },
                                                                                },
                                                                            },
                                                                        }}
                                                                    >
                                                                        <td
                                                                            style={{
                                                                                padding: "8px 8px",
                                                                                textAlign: "center",
                                                                                fontSize: "14px",
                                                                                border: "1px solid #ddd",
                                                                                backgroundColor:
                                                                                    colors.bg,
                                                                                color: colors.text,
                                                                                fontWeight:
                                                                                    month === 0
                                                                                        ? 500
                                                                                        : "normal",
                                                                                cursor: monthData
                                                                                    ? "default"
                                                                                    : "auto",
                                                                            }}
                                                                        >
                                                                            {monthData ? (
                                                                                <>
                                                                                    {Math.round(
                                                                                        monthData.rate
                                                                                    )}
                                                                                    %<br />
                                                                                    {
                                                                                        monthData.count
                                                                                    }
                                                                                </>
                                                                            ) : (
                                                                                ""
                                                                            )}
                                                                        </td>
                                                                    </MuiTooltip>
                                                                );
                                                            })}
                                                        </tr>
                                                    )
                                                );
                                            })()}
                                        </tbody>
                                        <tfoot>
                                            {(() => {
                                                const cohortMap = {};
                                                windowedCohortData.forEach((row) => {
                                                    if (!row.cohort) return;
                                                    if (!cohortMap[row.cohort]) {
                                                        cohortMap[row.cohort] = {
                                                            size: row.cohort_size,
                                                            months: {},
                                                        };
                                                    }
                                                    cohortMap[row.cohort].months[
                                                        row.months_since_start
                                                    ] = {
                                                        count: row.customers_retained,
                                                        rate: row.retention_rate,
                                                    };
                                                });
                                                const sortedCohorts = Object.keys(cohortMap)
                                                    .filter((k) => k)
                                                    .sort((a, b) => a.localeCompare(b));
                                                const formatCohort = (cohort) => {
                                                    const [year, month] = cohort.split("-");
                                                    const date = new Date(
                                                        year,
                                                        parseInt(month) - 1
                                                    );
                                                    return date.toLocaleDateString("en-US", {
                                                        month: "short",
                                                        year: "2-digit",
                                                    });
                                                };
                                                return (
                                                    <tr>
                                                        <th
                                                            style={{
                                                                paddingTop: "1rem",
                                                                paddingRight: "12px",
                                                                paddingBottom: "calc(1rem + 40px)",
                                                                paddingLeft: "12px",
                                                                textAlign: "left",
                                                                fontSize: "15px",
                                                                border: "none",
                                                                fontWeight: "normal",
                                                                position: "sticky",
                                                                left: 0,
                                                                backgroundColor: "white",
                                                                zIndex: 1,
                                                            }}
                                                        >
                                                            Month
                                                        </th>
                                                        {sortedCohorts.map((cohort) => (
                                                            <th
                                                                key={cohort}
                                                                style={{
                                                                    paddingTop: "1rem",
                                                                    paddingRight: "8px",
                                                                    paddingBottom:
                                                                        "calc(1rem + 40px)",
                                                                    paddingLeft: "8px",
                                                                    textAlign: "center",
                                                                    fontSize: "15px",
                                                                    border: "none",
                                                                    fontWeight: "normal",
                                                                    backgroundColor: "white",
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        display: "inline-block",
                                                                        transform: "rotate(-45deg)",
                                                                        whiteSpace: "nowrap",
                                                                    }}
                                                                >
                                                                    {formatCohort(cohort)}
                                                                </span>
                                                            </th>
                                                        ))}
                                                    </tr>
                                                );
                                            })()}
                                        </tfoot>
                                    </table>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                <Box sx={{ mt: 6, px: 1 }}>
                    <PipelineCharts
                        fetchData={fetchData}
                        selectedProducts={selectedProducts}
                        selectedProductFamily={selectedProductFamily}
                        selectedBusinessUnit={selectedBusinessUnit}
                    />
                </Box>

                {/* Debug table hidden
                    <Box sx={{ mt: 4, width: "100%", overflowX: "auto" }}>
                        <Typography
                            variant="body2"
                            sx={{ mb: 1, color: "text.secondary", fontStyle: "italic" }}
                        >
                            Note: This table is for debugging purposes only and is temporary.
                        </Typography>
                        <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                        <MUIDataTable
                            title=""
                            data={healthData}
                            columns={[
                                { name: "product_name", label: "product name" },
                                { name: "product_family_name", label: "product family name" },
                                { name: "period", label: "period" },
                                {
                                    name: "new_carr",
                                    label: "new carr",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "gross_new_carr",
                                    label: "gross new carr",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "total_carr",
                                    label: "total carr",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "total_churn",
                                    label: "total churn",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "total_churn_ctrl",
                                    label: "total churn ctrl",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "total_churn_unctrl",
                                    label: "total churn unctrl",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "new_customers",
                                    label: "new customers",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "total_customers",
                                    label: "total customers",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "yoy_carr_growth",
                                    label: "yoy carr growth",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "l3m_gross_churn",
                                    label: "l3m gross churn",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "grr",
                                    label: "grr",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "ttm_grr",
                                    label: "ttm grr",
                                    options: {
                                        setCellProps: () => ({ style: { textAlign: "right" } }),
                                    },
                                },
                                {
                                    name: "isGap",
                                    label: "isGap",
                                    options: { display: false, filter: false },
                                },
                            ]}
                            options={{
                                filterType: "dropdown",
                                responsive: "simple",
                                selectableRows: "none",
        searchAlwaysOpen: true,
        storageKey: "productHealthTable",
                                download: true,
                                print: true,
                                viewColumns: true,
                                filter: false,
                                search: false,
                                pagination: false,
                                sortOrder: { name: "periodLabel", direction: "desc" },
                                setTableProps: () => ({
                                    style: { tableLayout: "fixed", width: "100%" },
                                }),
                                setRowProps: (row, dataIndex) => {
                                    const rowData = healthData[dataIndex];
                                    if (rowData?.isGap) {
                                        return { style: { backgroundColor: "#f5f5f5" } };
                                    }
                                    return {};
                                },
                            }}
                        />
                        </div>
                    </Box>
                    */}
            </Box>
        </Box>
    );
};

export default ProductHealthTab;
